import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode} from 'react';

import * as analytics from 'analytics/Analytics';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';

export interface AntibodyInteractivityProps {
  hoveredRef: number | null;
  proteinIdsByRef: Record<number, string>;
  inaccessibleProteinIdsByRef: Record<number, string>;
  selectedRef: number | null;
  setHoveredRef: (newHoveredRef: number | null) => void;
  setSelectedRef: (newSelectedRefs: number | null) => void;
}

type InteractiveElement = 'g' | 'div';

export const INTERACTIVE_WRAPPER_DATA_ATTR = 'data-antibody-interactive-component-wrapper';

export interface InteractiveComponentWrapperProps {
  children: ReactNode;
  componentRef: number;
  interactivity?: AntibodyInteractivityProps;
  interactiveElement?: InteractiveElement;
  antibodyId?: string;
  componentLabel?: string;
}

export default function InteractiveComponentWrapper({
  children,
  componentRef,
  interactivity,
  interactiveElement = 'g',
  antibodyId,
  componentLabel,
}: InteractiveComponentWrapperProps): JSX.Element {
  if (interactivity == null) {
    return <>{children}</>;
  }

  const disableClick = interactivity.inaccessibleProteinIdsByRef[componentRef] != null;

  const props = {
    role: 'button',
    'aria-label': `Domain ${componentRef}`,
    className: css(disableClick ? styles.disabled : styles.group),
    onClick: (): void => {
      analytics.trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.CLICKED_COMPONENT_IN_ANTIBODY_MAP, {
        antibodyId,
        componentLabel,
      });
      if (disableClick) {
        return;
      }
      if (interactivity.selectedRef === componentRef) {
        interactivity.setSelectedRef(null);
      } else {
        interactivity.setSelectedRef(componentRef);
      }
    },
    onMouseOver: (): void => {
      interactivity.setHoveredRef(componentRef);
    },
    onMouseOut: (): void => {
      interactivity.setHoveredRef(null);
    },
    [INTERACTIVE_WRAPPER_DATA_ATTR]: true,
    ...getTestProps({
      component: 'InteractiveComponentWrapper',
      element: 'root',
      key: componentLabel,
    }),
  };

  const InteractiveElementTag = interactiveElement as React.ElementType;
  return <InteractiveElementTag {...props}>{children}</InteractiveElementTag>;
}

const styles = StyleSheet.create({
  group: {
    cursor: 'pointer',
  },
  disabled: {
    cursor: 'not-allowed',
  },
});
